import { useState, useEffect, useRef, useCallback, RefObject } from "react";

export interface UseInfiniteScrollOptions<T> {
  data: T[];
  initialLoadCount: number;
  loadMoreCount: number;
  onLoadMore?: (currentCount: number) => void | Promise<void>;
  tableContainerRef?: React.RefObject<HTMLDivElement | null>;
  hasMore?: boolean; // 외부에서 전달되는 hasMore 상태
  // 페이지네이션 관련 옵션 추가
  enablePagination?: boolean; // 페이지네이션 활성화 여부
  pageSize?: number; // 페이지 크기
  onPageChange?: (page: number) => void; // 페이지 변경 콜백
  currentPage?: number; // 현재 페이지
}

export interface UseInfiniteScrollReturn<T> {
  displayedData: T[];
  hasMore: boolean;
  loadingMore: boolean;
  setLoadingMore: (loading: boolean) => void;
  setHasMore: (hasMore: boolean) => void;
  loadMoreRef: (node: HTMLDivElement | null) => void;
  resetInfiniteScroll: () => void;
  // 페이지네이션 관련 반환값 추가
  currentPage: number;
  setCurrentPage: (page: number) => void;
  totalItems: number;
  setTotalItems: (total: number) => void;
  allData: T[];
  setAllData: (data: T[]) => void;
}

export function useInfiniteScroll<T>({
  data,
  initialLoadCount,
  loadMoreCount,
  onLoadMore,
  tableContainerRef,
  hasMore: externalHasMore,
  enablePagination = false,
  pageSize = 20,
  onPageChange,
  currentPage: externalCurrentPage,
}: UseInfiniteScrollOptions<T>): UseInfiniteScrollReturn<T> {
  const [displayedData, setDisplayedData] = useState<T[]>([]);
  const [currentCount, setCurrentCount] = useState(initialLoadCount);
  const [internalHasMore, setInternalHasMore] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);

  // 페이지네이션 관련 상태
  const [internalCurrentPage, setInternalCurrentPage] = useState(0);
  const [totalItems, setTotalItems] = useState(0);
  const [allData, setAllData] = useState<T[]>([]);

  // 외부에서 전달된 값과 내부 값을 동기화
  const actualCurrentPage = externalCurrentPage !== undefined ? externalCurrentPage : internalCurrentPage;
  const actualHasMore = externalHasMore !== undefined ? externalHasMore : internalHasMore;

  // 실제 DOM 노드를 보관하는 state와 callback ref
  const [loadMoreEl, setLoadMoreEl] = useState<HTMLDivElement | null>(null);
  const loadMoreRef = useCallback((node: HTMLDivElement | null) => {
    setLoadMoreEl(node);
  }, []);

  // 이전 data 참조 추적
  const prevDataRef = useRef<T[]>([]);

  // 초기 데이터 로드
  useEffect(() => {
    if (data.length === 0) {
      // data가 빈 배열이면 초기화
      setDisplayedData([]);
      setCurrentCount(0);
      prevDataRef.current = [];
      if (externalHasMore === undefined) {
        setInternalHasMore(false);
      }
      return;
    }

    // 이전 data와 현재 data의 첫 항목이 다르면 새로운 데이터
    const isNewData = prevDataRef.current.length === 0 ||
      (data.length > 0 && prevDataRef.current.length > 0 && data[0] !== prevDataRef.current[0]);

    if (isNewData) {
      const initialCount = Math.min(initialLoadCount, data.length);
      const initialData = data.slice(0, initialCount);

      setDisplayedData(initialData);
      setCurrentCount(initialCount);
      prevDataRef.current = data;

      // 외부 hasMore가 없을 때만 내부 로직으로 hasMore 계산
      if (externalHasMore === undefined) {
        setInternalHasMore(data.length > initialCount);
      }
    }
  }, [data, initialLoadCount, externalHasMore]);

  // data가 변경될 때마다 currentCount 업데이트 (더 많은 데이터가 로드된 경우)
  useEffect(() => {
    if (data.length > currentCount) {
      // 더 많은 데이터가 로드되었지만 아직 표시되지 않은 경우
      const newCount = Math.min(currentCount + loadMoreCount, data.length);
      const newData = data.slice(0, newCount);

      setDisplayedData(newData);
      setCurrentCount(newCount);
    }
  }, [data.length, currentCount, loadMoreCount]);

  // 페이지네이션이 활성화된 경우 데이터 누적 처리
  useEffect(() => {
    if (enablePagination && data && data.length > 0) {
      setAllData(prev => {
        // 중복 제거를 위해 새로운 데이터만 추가
        const newData = data.filter(
          (newItem: any) =>
            !prev.some((existingItem: any) => {
              // 기본적으로 id나 key를 사용하여 중복 제거
              const key = (newItem as any).id || (newItem as any).key || (newItem as any).stCompanyCode;
              const existingKey =
                (existingItem as any).id || (existingItem as any).key || (existingItem as any).stCompanyCode;
              return key === existingKey;
            })
        );
        return [...prev, ...newData];
      });
    }
  }, [data, enablePagination]);

  // 최신 상태를 참조하기 위한 ref
  const stateRef = useRef({
    currentCount,
    loadingMore,
    actualHasMore,
    dataLength: data.length,
    loadMoreCount,
    onLoadMore,
    enablePagination,
    allDataLength: allData.length,
  });

  // 상태가 변경될 때마다 ref 업데이트
  useEffect(() => {
    stateRef.current = {
      currentCount,
      loadingMore,
      actualHasMore,
      dataLength: data.length,
      loadMoreCount,
      onLoadMore,
      enablePagination,
      allDataLength: allData.length,
    };
  }, [
    currentCount,
    loadingMore,
    actualHasMore,
    data.length,
    loadMoreCount,
    onLoadMore,
    enablePagination,
    allData.length,
  ]);

  // 더 많은 데이터 로드 - 한 번만 생성되는 함수
  const loadMore = useCallback(async () => {
    const state = stateRef.current;

    if (state.loadingMore || !state.actualHasMore) {
      return;
    }

    // 페이지네이션이 활성화된 경우 외부 콜백 호출
    if (state.enablePagination && state.onLoadMore) {
      setLoadingMore(true);
      try {
        await state.onLoadMore(state.currentCount);
      } catch (error) {
        console.error("External onLoadMore error:", error);
      } finally {
        setLoadingMore(false);
      }
      return;
    }

    // 외부 hasMore가 true이고 onLoadMore가 있으면 외부 콜백 호출
    if (externalHasMore && state.onLoadMore) {
      setLoadingMore(true);
      try {
        await state.onLoadMore(state.currentCount);
      } catch (error) {
        console.error("External onLoadMore error:", error);
      } finally {
        setLoadingMore(false);
      }
      return;
    }

    // 내부 로직: 이미 모든 데이터를 로드했는지 확인
    if (state.currentCount >= state.dataLength) {
      setInternalHasMore(false);
      return;
    }

    setLoadingMore(true);

    try {
      const newCount = Math.min(state.currentCount + state.loadMoreCount, state.dataLength);
      const newData = data.slice(0, newCount);

      setDisplayedData(newData);
      setCurrentCount(newCount);

      // hasMore 상태 업데이트
      const newHasMore = newCount < state.dataLength;
      setInternalHasMore(newHasMore);

      // 외부 콜백 호출
      if (state.onLoadMore) {
        await state.onLoadMore(newCount);
      }
    } catch (error) {
      console.error("무한 스크롤 로드 중 오류:", error);
    } finally {
      setLoadingMore(false);
    }
  }, [data, externalHasMore, enablePagination]);

  // Intersection Observer 설정: loadMoreEl 이 생길 때마다 설정
  useEffect(() => {
    if (!loadMoreEl) {
      return;
    }

    const observer = new IntersectionObserver(
      entries => {
        const target = entries[0];

        if (target.isIntersecting) {
          loadMore();
        }
      },
      {
        root: (tableContainerRef?.current as HTMLDivElement) || null,
        rootMargin: "50px",
        threshold: 0.1,
      }
    );

    observer.observe(loadMoreEl);

    return () => {
      observer.unobserve(loadMoreEl);
      observer.disconnect();
    };
  }, [loadMoreEl, loadMore, tableContainerRef]);

  // 무한 스크롤 리셋 함수
  const resetInfiniteScroll = useCallback(() => {
    const initialCount = Math.min(initialLoadCount, data.length);
    const initialData = data.slice(0, initialCount);

    setDisplayedData(initialData);
    setCurrentCount(initialCount);

    if (externalHasMore === undefined) {
      setInternalHasMore(data.length > initialCount);
    }

    // 페이지네이션 관련 상태도 리셋
    if (enablePagination) {
      setInternalCurrentPage(0);
      setTotalItems(0);
      setAllData([]);
    }
  }, [data, initialLoadCount, externalHasMore, enablePagination]);

  // 페이지 변경 함수
  const setCurrentPage = useCallback(
    (page: number) => {
      if (enablePagination) {
        setInternalCurrentPage(page);
        onPageChange?.(page);
      }
    },
    [enablePagination, onPageChange]
  );

  return {
    displayedData,
    hasMore: actualHasMore,
    loadingMore,
    setLoadingMore,
    setHasMore: setInternalHasMore,
    loadMoreRef,
    resetInfiniteScroll,
    // 페이지네이션 관련 반환값
    currentPage: actualCurrentPage,
    setCurrentPage,
    totalItems,
    setTotalItems,
    allData,
    setAllData,
  };
}
